import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Modal,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Grid,
} from "@mui/material";
import { service } from "../model/service";
import Header from "../components/Header";

function ConfirmationGate({ isIta: isItaProp = true }) {
  const { token } = useParams();
  const [step, setStep] = useState("loading");
  const [isIta, setIsIta] = useState(isItaProp);
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const initCalledRef = useRef(false);

  useEffect(() => {
    if (initCalledRef.current) return;
    initCalledRef.current = true;

    service
      .initConfirmation(token)
      .then((data) => {
        if (data?.form?.language) {
          setIsIta(data.form.language === "it");
        }
        setStep("otp");
      })
      .catch(() => setStep("linkScaduto"));
  }, [token]);

  const handleConfirm = async () => {
    if (!otp.trim()) {
      setOtpError(isIta ? "Inserisci il codice OTP." : "Enter the OTP code.");
      return;
    }
    setLoading(true);
    setOtpError("");
    try {
      await service.verifyConfirmation(token, otp);
      setStep("success");
    } catch (err) {
      if (err.message === "OTP non corretto.") {
        setOtpError(isIta ? "OTP non corretto." : "Incorrect OTP.");
      } else if (err.message === "LINK_SCADUTO") {
        setStep("linkScaduto");
      } else {
        setStep("error");
      }
    } finally {
      setLoading(false);
    }
  };

  const withLayout = (content) => (
    <>
      <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
        <div style={{ position: "fixed", top: 0, width: "100%", zIndex: 1000 }}>
          <Header isIta={isIta} />
        </div>
      </div>
      <Modal open disableEscapeKeyDown>
        <Grid
          container
          item
          xs={11}
          md={5}
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#fff",
            maxHeight: "90%",
            borderRadius: "16px",
            zIndex: 1300,
            overflow: "auto",
            outline: "none",
          }}
        >
          {content}
        </Grid>
      </Modal>
    </>
  );

  if (step === "loading") {
    return withLayout(
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: "60px",
        }}
      >
        <CircularProgress sx={{ color: "#7B4FBF" }} />
      </Grid>
    );
  }

  if (step === "linkScaduto") {
    return withLayout(
      <>
        <Grid
          item
          xs={12}
          sx={{
            height: 60,
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #c6c6c6",
            px: "20px",
          }}
        >
          <Grid item xs={1.5} md={0.5} sx={{ ml: 0 }}>
            <div className="icon-modal-otp" />
          </Grid>
          <Grid item xs={10.5} md={11.5}>
            <Typography
              className="color-blue-dark"
              sx={{ fontSize: 18, fontWeight: 600, ml: 1 }}
            >
              {isIta ? "Procedura scaduta" : "Procedure expired"}
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            className="color-blue-dark"
            sx={{
              padding: "50px 20px",
              fontSize: { xs: "14px", md: "16px" },
            }}
          >
            {isIta ? (
              <>
                Questa procedura è inattiva in quanto il link di conferma è
                scaduto. Per assistenza scrivere a{" "}
                <a href="mailto:amministrazione@lexia.it" className="color-purple">
                  amministrazione@lexia.it
                </a>
                .
              </>
            ) : (
              <>
                This procedure is inactive as the confirmation link has expired.
                For assistance write to{" "}
                <a href="mailto:amministrazione@lexia.it" className="color-purple">
                  amministrazione@lexia.it
                </a>
                .
              </>
            )}
          </Typography>
        </Grid>
      </>
    );
  }

  if (step === "success") {
    return withLayout(
      <>
        <Grid
          item
          xs={12}
          sx={{
            height: 60,
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #c6c6c6",
          }}
        >
          <Grid item xs={1} style={{ marginLeft: "15px" }}>
            <div className="icon-check-success" />
          </Grid>
          <Grid item xs={11} sx={{ ml: { xs: 3, sm: 1 } }}>
            <Typography
              className="color-blue-dark"
              style={{ fontSize: "18px", fontWeight: 600 }}
            >
              {isIta ? "Dati confermati con successo" : "Data confirmed successfully"}
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            className="color-blue-dark"
            sx={{
              padding: "10px 20px 0px 20px",
              marginTop: "20px",
              fontSize: { xs: "14px", md: "16px" },
            }}
          >
            {isIta ? "I tuoi dati anti-riciclaggio sono stati confermati correttamente." : "Your anti-money laundering data has been confirmed correctly."}
          </Typography>
           <Typography
            className="color-blue-dark"
            sx={{
              padding: "0px 20px 0px 20px",
              fontSize: { xs: "14px", md: "16px" },
            }}
          >
           {isIta ? "Il tuo consenso alla conferma è stato salvato nei nostri archivi." : "Your confirmation consent has been saved in our records."}
          </Typography>
          <Typography
            className="color-blue-dark"
            sx={{
              padding: "0px 20px 0px 20px",
              fontSize: { xs: "14px", md: "16px" },
            }}
          >
            {isIta ? "Grazie per la collaborazione! Non sono necessarie ulteriori azioni da parte tua." : "Thank you for your cooperation! No further action is required on your part."}
          </Typography>
        </Grid>
        <Grid container item xs={12}>
          <Grid
            item
            xs={12}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "20px",
            }}
          >
            <Button
              className="button-bg-purple"
              sx={{ width: "120px" }}
              onClick={() => (window.location.href = "https://www.lexia.it")}
            >
              <Typography
                className="color-light"
                sx={{
                  textTransform: "initial",
                  fontSize: { xs: "14px", sm: "16px" },
                  fontWeight: 500,
                }}
              >
                {isIta ? "Chiudi" : "Close"}
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </>
    );
  }

  if (step === "error") {
    return withLayout(
      <>
        <Grid
          item
          xs={12}
          sx={{
            height: 60,
            display: "flex",
            alignItems: "center",
            borderBottom: "1px solid #c6c6c6",
            px: "20px",
          }}
        >
          <Grid item xs={1.5} md={0.5} sx={{ ml: 0 }}>
            <div className="icon-error-message" />
          </Grid>
          <Grid item xs={10.5} md={11.5}>
            <Typography
              className="color-blue-dark"
              sx={{ fontSize: 18, fontWeight: 600, ml: 1 }}
            >
              {isIta ? "Errore di conferma" : "Confirmation error"}
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            className="color-blue-dark"
            sx={{
              padding: "10px 20px 0px 20px",
              marginTop: "20px",
              fontSize: { xs: "14px", md: "16px" },
            }}
          >
            {isIta ? (
              <>
                Si è verificato un errore durante la conferma dei dati. Per
                assistenza scrivere a{" "}
                <a href="mailto:amministrazione@lexia.it" className="color-purple">
                  amministrazione@lexia.it
                </a>
                .
              </>
            ) : (
              <>
                An error occurred while confirming the data. For assistance
                write to{" "}
                <a href="mailto:amministrazione@lexia.it" className="color-purple">
                  amministrazione@lexia.it
                </a>
                .
              </>
            )}
          </Typography>
        </Grid>
        <Grid container item xs={12} style={{ marginTop: "20px" }}>
          <Grid
            item
            xs={12}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "20px",
            }}
          >
            <Button
              className="button-bg-purple"
              sx={{ width: "120px" }}
              onClick={() => (window.location.href = "https://www.lexia.it")}
            >
              <Typography
                className="color-light"
                sx={{
                  textTransform: "initial",
                  fontSize: { xs: "14px", sm: "16px" },
                  fontWeight: 500,
                }}
              >
                {isIta ? "Chiudi" : "Close"}
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </>
    );
  }

  // step === "otp"
  return withLayout(
    <>
      {/* Header */}
      <Grid
        item
        xs={12}
        sx={{
          height: 60,
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #c6c6c6",
          px: "20px",
        }}
      >
        <Grid item xs={1.5} md={0.5} sx={{ ml: 0 }}>
          <div className="icon-modal-otp" />
        </Grid>
        <Grid item xs={10.5} md={11.5}>
          <Typography
            className="color-blue-dark"
            sx={{ fontSize: 18, fontWeight: 600, ml: 1 }}
          >
            {isIta ? "Autenticazione" : "Authentication"}
          </Typography>
        </Grid>
      </Grid>

      {/* Body */}
      <Grid
        item
        xs={12}
        md={10}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        <Typography
          className="color-blue-dark"
          sx={{
            padding: "10px 15px 0px 15px",
            marginTop: "20px",
            fontSize: { xs: "14px", md: "16px" },
          }}
        >
          {isIta
            ? "Inserisci il codice OTP ricevuto alla tua casella email per confermare i tuoi dati anti-riciclaggio."
            : "Enter the OTP code received in your email inbox to confirm your anti-money laundering data."}
        </Typography>
        <Grid sx={{ padding: "20px 15px 0px 15px", minHeight: "90px" }}>
          <Typography className="color-blue-dark title-input">
            {isIta ? "Codice OTP" : "OTP Code"}
          </Typography>
          <TextField
            placeholder={isIta ? "Inserisci" : "Insert"}
            value={otp}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "");
              setOtp(val);
              setOtpError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
            error={!!otpError}
            helperText={otpError || " "}
            fullWidth
            inputProps={{ maxLength: 6 }}
            autoFocus
            FormHelperTextProps={{ sx: { marginLeft: "2px" } }}
          />
        </Grid>
      </Grid>

      {/* Footer */}
      <Grid container item xs={12} style={{ marginTop: "20px" }}>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "20px",
          }}
        >
          <Button
            className={
              loading || otp.trim().length !== 6
                ? "button-bg-purple-disable"
                : "button-bg-purple"
            }
            onClick={handleConfirm}
            disabled={loading || otp.trim().length !== 6}
            sx={{ height: "40px", width: { xs: "120px", md: "140px" } }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Typography
                className="color-light"
                sx={{
                  textTransform: "initial",
                  fontSize: { xs: "14px", sm: "16px" },
                  fontWeight: 500,
                }}
              >
                {isIta ? "Conferma" : "Confirm"}
              </Typography>
            )}
          </Button>
        </Grid>
      </Grid>
    </>
  );
}

export default ConfirmationGate;
